import type { SettingRow } from '@/hooks/useSettings';

interface ConfigSettingRowProps {
  row: SettingRow;
  onToggle: (row: SettingRow, isOn: boolean) => void;
}

// One on/off row. A row whose editable column is false shows its status without a switch.
export default function ConfigSettingRow({
  row,
  onToggle,
}: ConfigSettingRowProps) {
  // The scheduled jobs only check whether the value is truthy (authorizeCronJob.ts), so a value
  // stored as something other than true or false is shown the way the job reads it.
  const isOn = Boolean(row.value);

  return (
    <li
      className='flex flex-col md:flex-row md:items-start gap-2 md:gap-6 py-3 border-b border-base-300 last:border-b-0'
      data-testid={`setting-row-${row.key}`}
    >
      <div className='flex-1 min-w-0'>
        <h3 className='font-semibold'>{row.label}</h3>
        {row.description && (
          <p className='text-sm text-gray-400'>{row.description}</p>
        )}
        <p className='text-xs mt-1'>
          <code className='font-mono text-gray-300'>{row.key}</code>
        </p>
      </div>
      <div className='md:w-64 shrink-0 text-right'>
        <div className='inline-flex items-center gap-2'>
          <span className='text-sm'>{isOn ? 'On' : 'Off'}</span>
          {row.editable && (
            <input
              type='checkbox'
              className='toggle toggle-accent'
              aria-label={row.label}
              checked={isOn}
              onChange={() => onToggle(row, isOn)}
            />
          )}
        </div>
      </div>
    </li>
  );
}
